import { useState } from "react"
import { useNavigate, useSearch } from "@tanstack/react-router"
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleOff,
  Clock,
  Copy,
  ExternalLink,
  Minus,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Rocket,
  RotateCcw,
  Server,
  Settings,
  TrendingDown,
  TrendingUp,
  XCircle,
} from "lucide-react"
import toast from "react-hot-toast"

import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@aix/ui"

import { trpc } from "~/lib/trpc"

// ============================================================================
// TYPES
// ============================================================================

type HealthStatus = "analyzing" | "healthy" | "degraded" | "failed" | "unknown"

enum EDeployFeedScope {
  RELEASES = "releases",
  PRODUCTION = "production",
  ALL = "all",
}

interface DeployRow {
  id: string
  service: string
  projectName: string
  environment: string
  deployStatus: string
  healthStatus: string
  sha: string | null
  branch: string | null
  deployedBy: string | null
  externalId: string
  deployUrl: string | null
  rollbackUrl: string | null
  startedAt: Date | string
  completedAt: Date | string | null
  healthAnalyzedAt: Date | string | null
  createdAt: Date | string
  signalCount: number
}

interface Signal {
  id: string
  deployId: string
  signalType: string
  severity: string
  title: string
  detail: Record<string, unknown> | null
  detectedAt: Date | string
}

type AnalysisReceiptStatus = "clear" | "warning" | "unavailable" | "not_configured"

interface AnalysisReceipt {
  source: string
  status: AnalysisReceiptStatus
  checkedAt: Date | string
  summary: string
  evidence?: Record<string, unknown>
}

interface ProviderIngestionStatus {
  service: string
  status: "current" | "stale" | "failed" | "not_configured" | "not_observed"
  latestReconciledAt: Date | string | null
  reconciliationAgeMinutes: number | null
  reconciliationSource: "provider_api" | "deployment_artifact" | null
  reconciliationSummary: string | null
  reconciliationErrors: number | null
  latestEventAt: Date | string | null
  latestReceivedAt: Date | string | null
  receivedAgeMinutes: number | null
  freshnessExpectationMinutes: number | null
}

// ============================================================================
// HELPERS
// ============================================================================

function getHealthConfig(status: string) {
  switch (status as HealthStatus) {
    case "healthy":
      return { icon: CheckCircle2, color: "text-green-500", bg: "bg-green-500/10", label: "Healthy", emoji: "✅" }
    case "degraded":
      return { icon: AlertTriangle, color: "text-yellow-500", bg: "bg-yellow-500/10", label: "Degraded", emoji: "⚠️" }
    case "failed":
      return { icon: XCircle, color: "text-red-500", bg: "bg-red-500/10", label: "Failed", emoji: "🔴" }
    case "analyzing":
      return { icon: Clock, color: "text-blue-400", bg: "bg-blue-500/10", label: "Analyzing", emoji: "🔄" }
    default:
      return { icon: Clock, color: "text-muted-foreground", bg: "bg-secondary", label: "Unknown", emoji: "❓" }
  }
}

function getDeployHealthConfig(deploy: DeployRow) {
  if (deploy.environment !== "production") {
    return { icon: Minus, color: "text-muted-foreground", bg: "bg-secondary", label: "Not evaluated", emoji: "—" }
  }
  if (deploy.deployStatus === "failed") {
    return { icon: XCircle, color: "text-destructive", bg: "bg-destructive/10", label: "Provider failed", emoji: "🔴" }
  }
  return getHealthConfig(deploy.healthStatus)
}

function getSignalSeverityColor(severity: string) {
  if (severity === "critical") return "text-red-500"
  if (severity === "warn") return "text-yellow-500"
  return "text-blue-400"
}

function getReceiptStatusConfig(status: AnalysisReceiptStatus) {
  switch (status) {
    case "clear":
      return { icon: CheckCircle2, label: "Clear", className: "bg-green-500/10 text-green-500" }
    case "warning":
      return { icon: AlertTriangle, label: "Warning", className: "bg-yellow-500/10 text-yellow-500" }
    case "unavailable":
      return { icon: AlertCircle, label: "Unavailable", className: "bg-destructive/10 text-destructive" }
    case "not_configured":
      return { icon: CircleOff, label: "Not configured", className: "bg-secondary text-muted-foreground" }
  }
}

function getSignalSourceLabel(source: string): string {
  const labels: Record<string, string> = {
    api_monitor: "API monitors",
    uptime: "Uptime",
    axiom_errors: "Axiom",
    sentry_issues: "Sentry",
  }
  return labels[source] ?? source
}

function formatMinutesAgo(minutes: number | null): string {
  if (minutes === null) return "Never observed"
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

function timeAgo(dateRaw: Date | string | null | undefined): string {
  if (!dateRaw) return "—"
  const date = typeof dateRaw === "string" ? new Date(dateRaw) : dateRaw
  const diffMs = Date.now() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return "just now"
  if (diffMin < 60) return `${diffMin}m ago`
  const diffH = Math.floor(diffMin / 60)
  if (diffH < 24) return `${diffH}h ago`
  return `${Math.floor(diffH / 24)}d ago`
}

function formatDuration(start: Date | string, end: Date | string | null): string {
  if (!end) return "running"
  const s = typeof start === "string" ? new Date(start) : start
  const e = typeof end === "string" ? new Date(end) : end
  const diffMs = e.getTime() - s.getTime()
  const mins = Math.floor(diffMs / 60000)
  const secs = Math.floor((diffMs % 60000) / 1000)
  if (mins > 0) return `${mins}m ${secs}s`
  return `${secs}s`
}

function getServiceEmoji(service: string): string {
  const map: Record<string, string> = {
    vercel: "▲",
    render: "🟣",
    cloudflare: "🟠",
    eas_build: "📱",
    eas_ota: "📡",
    github_actions: "⚙️",
    manual: "🖐️",
  }
  return map[service] ?? "🚀"
}

function copyToClipboard(text: string, label: string) {
  void navigator.clipboard.writeText(text).then(() => {
    toast.success(`Copied ${label}`)
  })
}

const RELEASE_WAVE_WINDOW_MS = 30 * 60 * 1000
const RELEASE_PAIRING_QUERY_LIMIT = 100

function getDeployStartedAtMs(deploy: DeployRow): number {
  return new Date(deploy.startedAt).getTime()
}

function groupDeploysByReleaseWave(deploys: DeployRow[]): DeployRow[][] {
  const groups: DeployRow[][] = []
  for (const deploy of deploys) {
    if (!deploy.sha) {
      groups.push([deploy])
      continue
    }
    const group = groups.find((candidate) => {
      const anchor = candidate[0]
      return (
        anchor?.sha === deploy.sha &&
        anchor.environment === deploy.environment &&
        Math.abs(getDeployStartedAtMs(anchor) - getDeployStartedAtMs(deploy)) <= RELEASE_WAVE_WINDOW_MS
      )
    })
    if (group) group.push(deploy)
    else groups.push([deploy])
  }
  return groups
}

function findReleasePairingWave(wave: DeployRow[], pairingWaves: DeployRow[][]): DeployRow[] | null {
  const anchor = wave[0]
  if (!anchor?.sha) return null
  return (
    pairingWaves.find((candidate) => {
      const candidateAnchor = candidate[0]
      return (
        candidateAnchor?.sha === anchor.sha &&
        candidateAnchor.environment === anchor.environment &&
        Math.abs(getDeployStartedAtMs(candidateAnchor) - getDeployStartedAtMs(anchor)) <= RELEASE_WAVE_WINDOW_MS
      )
    }) ?? null
  )
}

function hasReleasePairingCoverage(wave: DeployRow[], pairingDeploys: DeployRow[]): boolean {
  if (pairingDeploys.length < RELEASE_PAIRING_QUERY_LIMIT) return true
  const anchor = wave[0]
  if (!anchor) return false
  const oldestPairingTimestamp = Math.min(...pairingDeploys.map(getDeployStartedAtMs))
  return getDeployStartedAtMs(anchor) >= oldestPairingTimestamp
}

function ProviderIngestionPanel() {
  const utils = trpc.useUtils()
  const { data, isLoading, isError, error, refetch } = trpc.deployFeed.getProviderIngestionStatus.useQuery(undefined, {
    refetchInterval: 60_000,
  })
  const reconcileMutation = trpc.deployFeed.reconcileProviders.useMutation({
    onSuccess: (result) => {
      void Promise.all([
        utils.deployFeed.getProviderIngestionStatus.invalidate(),
        utils.deployFeed.getStats.invalidate(),
        utils.deployFeed.list.invalidate(),
      ])
      if (result.errors > 0) {
        toast.error(`Provider reconciliation completed with ${result.errors} error(s)`)
      } else {
        toast.success("All deploy providers reconciled")
      }
    },
    onError: (error) => toast.error(`Provider reconciliation failed: ${error.message}`),
  })

  if (isLoading) {
    return (
      <div data-testid="provider-ingestion-status" className="rounded-lg border border-border bg-secondary/30 p-3">
        <div className="text-sm text-muted-foreground">Loading provider ingestion evidence...</div>
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div
        data-testid="provider-ingestion-status"
        className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-3"
      >
        <div className="flex items-start gap-2">
          <AlertCircle className="mt-0.5 h-4 w-4 text-destructive" />
          <div>
            <div className="text-sm font-medium text-destructive">Provider ingestion evidence unavailable</div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {error?.message ?? "The provider receipt status could not be loaded."}
            </p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    )
  }

  const providers = data.providers as ProviderIngestionStatus[]
  const actionableProviders = providers.filter((provider) => provider.status !== "current")

  return (
    <div
      data-testid="provider-ingestion-status"
      className={`rounded-lg border p-3 ${
        actionableProviders.length > 0 ? "border-destructive/40 bg-destructive/5" : "border-border bg-secondary/30"
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-2">
          {actionableProviders.length > 0 ? (
            <AlertCircle className="mt-0.5 h-4 w-4 text-destructive" />
          ) : (
            <Server className="mt-0.5 h-4 w-4 text-green-500" />
          )}
          <div>
            <div className="text-sm font-medium text-foreground">
              {actionableProviders.length > 0
                ? `${actionableProviders.length} provider ingestion path(s) need attention`
                : "All deploy ingestion paths are current"}
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Sync health is measured separately from deploy activity, so a quiet provider can still be healthy.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {providers.map((provider) => {
            const statusClassName =
              provider.status === "stale" || provider.status === "failed"
                ? "bg-destructive/10 text-destructive"
                : provider.status === "not_configured" || provider.status === "not_observed"
                  ? "bg-secondary text-muted-foreground"
                  : "bg-green-500/10 text-green-500"
            let syncLabel = `Synced ${formatMinutesAgo(provider.reconciliationAgeMinutes)}`
            if (provider.status === "not_configured") {
              syncLabel = "Not configured"
            } else if (provider.status === "failed") {
              syncLabel = `Failed ${formatMinutesAgo(provider.reconciliationAgeMinutes)}`
            } else if (provider.status === "stale") {
              syncLabel = `Last sync ${formatMinutesAgo(provider.reconciliationAgeMinutes)}`
            } else if (provider.reconciliationAgeMinutes === null) {
              syncLabel =
                provider.freshnessExpectationMinutes === null
                  ? `Webhook ${formatMinutesAgo(provider.receivedAgeMinutes)}`
                  : "Never synced"
            }
            return (
              <div
                key={provider.service}
                className={`rounded px-2 py-1.5 text-xs ${statusClassName}`}
                title={provider.reconciliationSummary ?? "Webhook activity receipt"}
              >
                <div className="font-medium">{provider.service}</div>
                <div className="opacity-80">{syncLabel}</div>
                <div className="opacity-70">Deploy {formatMinutesAgo(provider.receivedAgeMinutes)}</div>
              </div>
            )
          })}
          <Button
            size="sm"
            variant="outline"
            loading={reconcileMutation.isLoading}
            onClick={() => reconcileMutation.mutate()}
          >
            <RefreshCw className="mr-1 h-3.5 w-3.5" />
            Reconcile all
          </Button>
        </div>
      </div>
    </div>
  )
}

function ReleaseWaveHeader({
  wave,
  pairingEvidence,
}: {
  wave: DeployRow[]
  pairingEvidence: "available" | "pending" | "unavailable"
}) {
  const sha = wave[0]?.sha
  if (!sha) return null

  const webDeploys = wave.filter((deploy) => deploy.projectName === "vidmax-vite")
  const apiDeploys = wave.filter((deploy) => deploy.projectName === "vidmax-api")
  const isVidMaxRelease = webDeploys.length > 0 || apiDeploys.length > 0
  const isMultiDeployWave = wave.length > 1
  if (!isVidMaxRelease && !isMultiDeployWave) return null
  let apiLabel = "deploy missing"
  let apiClassName = "bg-yellow-500/10 text-yellow-500"
  if (apiDeploys.length > 0) {
    apiLabel = `observed (${apiDeploys.length})`
    apiClassName = "bg-green-500/10 text-green-500"
  } else if (pairingEvidence === "pending") {
    apiLabel = "pairing pending"
    apiClassName = "bg-secondary text-muted-foreground"
  } else if (pairingEvidence === "unavailable") {
    apiLabel = "pairing unavailable"
    apiClassName = "bg-destructive/10 text-destructive"
  }

  return (
    <div
      data-testid={isVidMaxRelease ? "vidmax-release-wave" : undefined}
      className="rounded-lg border border-border bg-secondary/30 px-3 py-2"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">{isVidMaxRelease ? "VidMax release" : "Release wave"}</span>
          <span className="rounded bg-secondary px-1.5 py-0.5 font-mono">{sha.slice(0, 7)}</span>
          <span>
            {wave.length} {wave.length === 1 ? "deploy" : "deploys"}
          </span>
        </div>
        {isVidMaxRelease && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span
              className={`rounded px-2 py-1 ${
                webDeploys.length > 0 ? "bg-green-500/10 text-green-500" : "bg-secondary text-muted-foreground"
              }`}
            >
              Web {webDeploys.length > 0 ? `observed (${webDeploys.length})` : "not observed"}
            </span>
            <span
              data-testid={
                apiDeploys.length === 0 && pairingEvidence === "available" ? "vidmax-api-deploy-missing" : undefined
              }
              className={`rounded px-2 py-1 ${apiClassName}`}
            >
              API {apiLabel}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// SIGNAL DETAIL PANEL
// ============================================================================

function SignalDetailPanel({ deployId, healthEligible }: { deployId: string; healthEligible: boolean }) {
  const {
    data: analysis,
    isLoading,
    isError,
    error,
    refetch,
  } = trpc.deployFeed.getAnalysis.useQuery({ deployId }, { enabled: healthEligible })

  if (!healthEligible) {
    return <div className="p-4 text-sm text-muted-foreground">Health correlation runs on production releases only.</div>
  }

  if (isLoading) {
    return <div className="p-4 text-sm text-muted-foreground">Loading signals...</div>
  }

  if (isError || !analysis) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div>
          <div className="text-sm font-medium text-destructive">Health evidence unavailable</div>
          <p className="mt-1 text-xs text-muted-foreground">
            {error?.message ?? "The analysis receipts and anomaly signals could not be loaded."}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    )
  }

  const signals = analysis.signals as Signal[]
  const receipts = analysis.receipts as AnalysisReceipt[]

  return (
    <div className="space-y-4 p-4">
      <div>
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Correlation source receipts
        </div>
        {receipts.length > 0 ? (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {receipts.map((receipt) => {
              const status = getReceiptStatusConfig(receipt.status)
              const StatusIcon = status.icon
              return (
                <div key={receipt.source} className="rounded-md border border-border bg-background p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-foreground">{getSignalSourceLabel(receipt.source)}</span>
                    <span className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-xs ${status.className}`}>
                      <StatusIcon className="h-3 w-3" />
                      {status.label}
                    </span>
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{receipt.summary}</p>
                  <div className="mt-2 text-xs text-muted-foreground">Checked {timeAgo(receipt.checkedAt)}</div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="rounded-md border border-border bg-background p-3 text-sm text-muted-foreground">
            Source receipts are pending or unavailable for this deployment.
          </div>
        )}
      </div>

      <div>
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Detected anomalies</div>
        {signals.length === 0 ? (
          <div className="rounded-md bg-green-500/5 p-3 text-sm text-muted-foreground">
            No anomalies were detected by the completed source checks.
          </div>
        ) : (
          <div className="space-y-3">
            {signals.map((signal) => (
              <div key={signal.id} className="rounded bg-secondary p-3">
                <div className={`text-sm font-medium ${getSignalSeverityColor(signal.severity)}`}>{signal.title}</div>
                {signal.detail && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                      Raw detail
                    </summary>
                    <pre className="mt-1 max-h-32 overflow-auto text-xs text-muted-foreground">
                      {JSON.stringify(signal.detail, null, 2)}
                    </pre>
                  </details>
                )}
                <div className="mt-1 text-xs text-muted-foreground">{timeAgo(signal.detectedAt)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// DEPLOY ROW
// ============================================================================

function DeployRow({
  deploy,
  onReanalyze,
  isReanalyzing,
}: {
  deploy: DeployRow
  onReanalyze: (id: string) => void
  isReanalyzing: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const panelId = `deploy-panel-${deploy.id}`
  const isSucceededProduction = deploy.environment === "production" && deploy.deployStatus === "succeeded"
  const healthEligible = isSucceededProduction
  const health = getDeployHealthConfig(deploy)
  const HealthIcon = health.icon
  const shaShort = deploy.sha ? deploy.sha.slice(0, 7) : null

  const handleRollback = () => {
    if (!deploy.rollbackUrl) return
    toast.success(`Opening rollback for ${deploy.projectName}...`)
    window.open(deploy.rollbackUrl, "_blank")
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-controls={panelId}
        className="w-full cursor-pointer p-4 text-left transition-colors hover:bg-secondary/50"
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            setExpanded((v) => !v)
          }
        }}
      >
        <div className="flex items-start gap-3">
          <div
            className={`flex shrink-0 items-center gap-1.5 rounded px-2 py-0.5 text-xs font-semibold ${health.bg} ${health.color}`}
          >
            <HealthIcon className="h-3.5 w-3.5" />
            {health.label.toUpperCase()}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-foreground">
                {getServiceEmoji(deploy.service)} {deploy.projectName}
              </span>
              {shaShort && (
                <span className="rounded bg-secondary px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                  {shaShort}
                </span>
              )}
              {deploy.branch && <span className="text-xs text-muted-foreground">{deploy.branch}</span>}
              <span className="text-xs text-muted-foreground">{deploy.environment}</span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <span>{timeAgo(deploy.startedAt)}</span>
              {deploy.completedAt && <span>took {formatDuration(deploy.startedAt, deploy.completedAt)}</span>}
              {deploy.deployedBy && <span>by {deploy.deployedBy}</span>}
              {deploy.signalCount > 0 && <span className="text-yellow-500">{deploy.signalCount} signal(s)</span>}
              {healthEligible && deploy.healthStatus === "analyzing" && (
                <span className="animate-pulse text-blue-400">analyzing...</span>
              )}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {deploy.deployUrl && (
              <a
                href={deploy.deployUrl}
                target="_blank"
                rel="noreferrer"
                className="text-muted-foreground hover:text-foreground"
                title="View deploy"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
            {healthEligible && deploy.rollbackUrl && (
              <button
                type="button"
                className="rounded border border-orange-400/30 px-2 py-0.5 text-xs font-medium text-orange-400 hover:border-orange-300/50 hover:text-orange-300"
                onClick={handleRollback}
                title="Rollback this deploy"
              >
                ↩ Rollback
              </button>
            )}
            {healthEligible && (
              <button
                type="button"
                aria-label={`Re-run health analysis for ${deploy.projectName}`}
                className={`text-muted-foreground hover:text-foreground ${isReanalyzing ? "animate-spin" : ""}`}
                onClick={() => onReanalyze(deploy.id)}
                title="Re-run health analysis"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
            )}
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </div>
      </div>

      {expanded && (
        <div id={panelId} role="region" className="border-t border-border bg-secondary/20">
          <SignalDetailPanel deployId={deploy.id} healthEligible={healthEligible} />
        </div>
      )}
    </div>
  )
}

// ============================================================================
// DORA METRICS PANEL
// ============================================================================

function DORAPanel() {
  const [isOpen, setIsOpen] = useState(false)
  const {
    data: dora,
    isLoading,
    isError,
    error,
    refetch,
  } = trpc.deployFeed.getDORAMetrics.useQuery(undefined, {
    enabled: isOpen,
  })

  if (!dora && !isLoading && !isOpen) {
    return (
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between text-muted-foreground">
            <span className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              30-day deployment insights
            </span>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </CollapsibleTrigger>
      </Collapsible>
    )
  }

  const TrendIcon = dora
    ? dora.deployFrequency.trend === "up"
      ? TrendingUp
      : dora.deployFrequency.trend === "down"
        ? TrendingDown
        : Minus
    : Minus
  const trendColor = dora
    ? dora.deployFrequency.trend === "up"
      ? "text-green-500"
      : dora.deployFrequency.trend === "down"
        ? "text-red-500"
        : "text-muted-foreground"
    : "text-muted-foreground"

  const cfrColor = dora
    ? dora.changeFailureRate.rate <= 10
      ? "text-green-500"
      : dora.changeFailureRate.rate <= 25
        ? "text-yellow-500"
        : "text-red-500"
    : "text-muted-foreground"

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="rounded-lg border border-border">
      <CollapsibleTrigger asChild>
        <Button variant="ghost" className="w-full justify-between text-muted-foreground">
          <span className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            30-day production deployment insights
          </span>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t border-border p-4">
        {isError ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-destructive">Deployment insights unavailable</div>
              <p className="mt-1 text-xs text-muted-foreground">
                {error?.message ?? "The 30-day production metrics could not be loaded."}
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={() => void refetch()}>
              Retry
            </Button>
          </div>
        ) : isLoading || !dora ? (
          <div className="text-sm text-muted-foreground">Loading DORA metrics...</div>
        ) : (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <div>
              <div className="mb-1 text-xs text-muted-foreground">Deploy Frequency</div>
              <div className="flex items-center gap-1">
                <span className="text-lg font-bold text-foreground">{dora.deployFrequency.perWeek}/wk</span>
                <TrendIcon className={`h-4 w-4 ${trendColor}`} />
              </div>
              <div className="text-xs text-muted-foreground">{dora.deployFrequency.perDay.toFixed(1)}/day</div>
            </div>
            <div>
              <div className="mb-1 text-xs text-muted-foreground">Change Failure Rate</div>
              <div className={`text-lg font-bold ${cfrColor}`}>{dora.changeFailureRate.rate}%</div>
              <div className="text-xs text-muted-foreground">
                {dora.changeFailureRate.failed} of {dora.changeFailureRate.total} evaluated deploys
              </div>
            </div>
            <div>
              <div className="mb-1 text-xs text-muted-foreground">MTTR</div>
              <div className="text-lg font-bold text-foreground">
                {dora.mttr.avgMinutes != null ? `${dora.mttr.avgMinutes}m` : "—"}
              </div>
              <div className="text-xs text-muted-foreground">
                {dora.mttr.samples > 0 ? `${dora.mttr.samples} sample(s)` : "No recovery data yet"}
              </div>
            </div>
            <div>
              <div className="mb-1 text-xs text-muted-foreground">Most Active Project</div>
              <div className="truncate text-sm font-bold text-foreground">
                {dora.projectBreakdown[0]?.projectName ?? "—"}
              </div>
              <div className="text-xs text-muted-foreground">{dora.projectBreakdown[0]?.totalDeploys ?? 0} deploys</div>
            </div>
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  )
}

// ============================================================================
// PROJECT SUMMARY CARDS
// ============================================================================

function ProjectSummaryCards() {
  const { data: summaries, isLoading } = trpc.deployFeed.getProjectSummaries.useQuery()

  if (isLoading || !summaries || summaries.length === 0) return null

  return (
    <div>
      <div className="mb-2 text-sm font-medium text-muted-foreground">Projects (7d)</div>
      <div className="flex flex-wrap gap-2">
        {summaries.map(
          (s: {
            projectName: string
            lastHealthStatus: string
            last7DayHealthPct: number
            deployCount7d: number
            signalCount7d: number
            lastDeployAt: Date | string
          }) => {
            const health = getHealthConfig(s.lastHealthStatus)
            const HealthIcon = health.icon
            return (
              <div
                key={s.projectName}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${health.bg} border-border text-sm`}
              >
                <HealthIcon className={`h-3.5 w-3.5 ${health.color}`} />
                <span className="font-medium text-foreground">{s.projectName}</span>
                <span className="text-xs text-muted-foreground">{s.last7DayHealthPct}% healthy</span>
                <span className="text-xs text-muted-foreground">
                  {s.deployCount7d} {s.deployCount7d === 1 ? "deploy" : "deploys"}
                </span>
                {s.signalCount7d > 0 && <span className="text-xs text-yellow-500">{s.signalCount7d}⚡</span>}
              </div>
            )
          },
        )}
      </div>
    </div>
  )
}

// ============================================================================
// WEBHOOK SETUP TAB
// ============================================================================

const API_BASE = "https://api.aixlabs.co/webhooks/deploy"

function WebhookSetupTab() {
  const webhooks = [
    {
      name: "Vercel",
      emoji: "▲",
      url: `${API_BASE}/vercel`,
      auth: "HMAC-SHA1 via `x-vercel-signature` header",
      envVar: "VERCEL_WEBHOOK_SECRET",
      setup: [
        "Go to Project Settings → Webhooks → Add Webhook",
        `URL: ${API_BASE}/vercel`,
        "Events: deployment.succeeded, deployment.error, deployment.canceled",
        "Copy signing secret → add to Infisical as VERCEL_WEBHOOK_SECRET",
      ],
    },
    {
      name: "Render",
      emoji: "🟣",
      url: `${API_BASE}/render?secret={RENDER_WEBHOOK_SECRET}`,
      auth: "Secret query param",
      envVar: "RENDER_WEBHOOK_SECRET",
      setup: [
        "Optional fast path: go to Service Settings → Webhooks → Add Webhook",
        `URL: ${API_BASE}/render?secret=YOUR_SECRET`,
        "Events: Deploy Started, Deploy Succeeded, Deploy Failed",
        "The five-minute Render reconciliation remains the source of truth if a webhook is missed.",
      ],
    },
    {
      name: "GitHub Actions",
      emoji: "⚙️",
      url: `${API_BASE}/github`,
      auth: "HMAC-SHA256 via `x-hub-signature-256` header",
      envVar: "GITHUB_WEBHOOK_SECRET",
      setup: [
        "Go to Repo Settings → Webhooks → Add Webhook",
        `URL: ${API_BASE}/github`,
        "Content type: application/json",
        "Events: Workflow runs",
        "Copy secret → add to Infisical as GITHUB_WEBHOOK_SECRET",
      ],
    },
    {
      name: "Cloudflare Workers",
      emoji: "🟠",
      url: `${API_BASE}/cloudflare?secret={CLOUDFLARE_WEBHOOK_SECRET}`,
      auth: "Secret query param",
      envVar: "CLOUDFLARE_WEBHOOK_SECRET",
      setup: [
        "Add a step to your GitHub Actions wrangler deploy workflow:",
        'curl -X POST "' + API_BASE + '/cloudflare?secret=${{ secrets.CLOUDFLARE_WEBHOOK_SECRET }}" \\',
        '  -d \'{"projectName":"your-worker","sha":"${{ github.sha }}","deployStatus":"succeeded"}\'',
      ],
    },
  ]

  const envVars = [
    { name: "VERCEL_WEBHOOK_SECRET", status: "needs adding", desc: "Vercel HMAC-SHA1 signing secret" },
    { name: "RENDER_WEBHOOK_SECRET", status: "needs adding", desc: "Render auth secret (any string)" },
    { name: "RENDER_KEY", status: "exists", desc: "Render API reconciliation credential" },
    { name: "GITHUB_WEBHOOK_SECRET", status: "needs adding", desc: "GitHub HMAC-SHA256 secret" },
    { name: "CLOUDFLARE_WEBHOOK_SECRET", status: "needs adding", desc: "CF auth secret (any string)" },
    { name: "AXIOM_API_TOKEN", status: "exists", desc: "Query error rates (already set)" },
    { name: "SENTRY_AUTH_TOKEN", status: "exists", desc: "Query Sentry issues (already set)" },
    { name: "EXPO_TOKEN", status: "exists", desc: "Poll EAS builds + OTAs (set in CI)" },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h2 className="mb-1 text-lg font-semibold text-foreground">Webhook Endpoints</h2>
        <p className="text-sm text-muted-foreground">
          Configure these in your CI/CD services to start ingesting deploys.
        </p>
      </div>

      <div className="space-y-4">
        {webhooks.map((wh) => (
          <div key={wh.name} className="rounded-lg border border-border bg-secondary p-4">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span>{wh.emoji}</span>
                <span className="font-medium text-foreground">{wh.name}</span>
              </div>
              <button
                type="button"
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => copyToClipboard(wh.url, `${wh.name} URL`)}
              >
                <Copy className="h-3 w-3" />
                Copy URL
              </button>
            </div>
            <div className="mb-3 break-all rounded border border-border bg-background px-2 py-1.5 font-mono text-xs text-muted-foreground">
              POST {wh.url}
            </div>
            <div className="mb-1 text-xs text-muted-foreground">
              <span className="text-foreground">Auth:</span> {wh.auth}
              {" · "}
              <span className="text-foreground">Env var:</span>{" "}
              <code className="rounded bg-background px-1 font-mono">{wh.envVar}</code>
            </div>
            <details className="mt-2">
              <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                Setup instructions
              </summary>
              <ol className="mt-2 space-y-1">
                {wh.setup.map((step, i) => (
                  <li key={i} className="flex gap-2 text-xs text-muted-foreground">
                    <span className="shrink-0 font-medium text-foreground">{i + 1}.</span>
                    <code className="break-all font-mono">{step}</code>
                  </li>
                ))}
              </ol>
            </details>
          </div>
        ))}
      </div>

      <div>
        <div className="mb-2 flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">🟣 Render + 📦 EAS</span>
          <span className="rounded bg-green-500/10 px-2 py-0.5 text-xs text-green-500">Auto-polled every 5 min</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Provider reconciliation backfills missed events. Render uses{" "}
          <code className="rounded bg-secondary px-1 font-mono">RENDER_KEY</code>; EAS uses{" "}
          <code className="rounded bg-secondary px-1 font-mono">EXPO_TOKEN</code>.
        </p>
      </div>

      <div>
        <h3 className="mb-3 text-sm font-semibold text-foreground">Required Environment Variables</h3>
        <p className="mb-3 text-xs text-muted-foreground">
          Add to Infisical → <code className="font-mono">aixinternal-api</code> → production
        </p>
        <div className="space-y-2">
          {envVars.map((v) => (
            <div key={v.name} className="flex items-center gap-3 text-xs">
              <code className="w-56 shrink-0 rounded bg-secondary px-2 py-1 font-mono text-foreground">{v.name}</code>
              <span
                className={`rounded px-1.5 py-0.5 ${v.status === "exists" ? "bg-green-500/10 text-green-500" : "bg-yellow-500/10 text-yellow-500"}`}
              >
                {v.status === "exists" ? "✓ exists" : "⚠ needs adding"}
              </span>
              <span className="text-muted-foreground">{v.desc}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// MAIN SCREEN
// ============================================================================

type Tab = "timeline" | "setup"

function DeployFeedScreen() {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { tab?: string; scope?: string }
  const activeTab: Tab = search.tab === "setup" ? "setup" : "timeline"
  const scope =
    search.scope === EDeployFeedScope.PRODUCTION || search.scope === EDeployFeedScope.ALL
      ? search.scope
      : EDeployFeedScope.RELEASES

  const setActiveTab = (tab: Tab) => {
    void navigate({
      to: "/devtools/deploy-feed",
      search: (s) => {
        return { ...s, tab }
      },
    })
  }
  const setScope = (nextScope: EDeployFeedScope) => {
    void navigate({
      to: "/devtools/deploy-feed",
      search: (current) => ({ ...current, scope: nextScope }),
    })
  }
  const [filterService, setFilterService] = useState("")
  const [filterProject, setFilterProject] = useState("")
  const [filterHealth, setFilterHealth] = useState("")
  const [showManualForm, setShowManualForm] = useState(false)
  const [manualProjectName, setManualProjectName] = useState("")
  const [manualSha, setManualSha] = useState("")
  const [reanalyzingIds, setReanalyzingIds] = useState<Set<string>>(new Set())

  const utils = trpc.useUtils()

  const {
    data: deploys,
    isLoading,
    refetch,
  } = trpc.deployFeed.list.useQuery(
    {
      limit: 50,
      service: filterService || undefined,
      projectName: filterProject || undefined,
      healthStatus: filterHealth || undefined,
      scope,
    },
    { refetchInterval: 30_000 },
  )

  const { data: stats } = trpc.deployFeed.getStats.useQuery(undefined, { refetchInterval: 60_000 })
  const {
    data: releasePairingDeploys,
    isLoading: isReleasePairingLoading,
    isError: isReleasePairingError,
  } = trpc.deployFeed.list.useQuery(
    { limit: RELEASE_PAIRING_QUERY_LIMIT, scope: EDeployFeedScope.RELEASES },
    { refetchInterval: 30_000 },
  )
  const hasVisibleDeploys = (deploys?.length ?? 0) > 0
  const { data: allActivityProbe } = trpc.deployFeed.list.useQuery(
    { limit: 1, scope: EDeployFeedScope.ALL },
    { enabled: !isLoading && !hasVisibleDeploys },
  )

  const reanalyzeMutation = trpc.deployFeed.triggerAnalysis.useMutation({
    onSuccess: (_, variables: { deployId: string }) => {
      setReanalyzingIds((prev) => {
        const next = new Set(prev)
        next.delete(variables.deployId)
        return next
      })
      toast.success("Health analysis triggered")
      void utils.deployFeed.list.invalidate()
      void utils.deployFeed.getAnalysis.invalidate({ deployId: variables.deployId })
    },
    onError: (err, variables: { deployId: string }) => {
      setReanalyzingIds((prev) => {
        const next = new Set(prev)
        next.delete(variables.deployId)
        return next
      })
      toast.error(err.message || "Failed to trigger analysis")
    },
  })

  const ingestManualMutation = trpc.deployFeed.ingestManual.useMutation({
    onSuccess: () => {
      toast.success("Deploy event added")
      setShowManualForm(false)
      setManualProjectName("")
      setManualSha("")
      void refetch()
    },
    onError: (err) => toast.error(err.message || "Failed to add deploy"),
  })

  const handleReanalyze = (deployId: string) => {
    setReanalyzingIds((prev) => new Set(prev).add(deployId))
    reanalyzeMutation.mutate({ deployId })
  }

  const handleAddManual = () => {
    if (!manualProjectName.trim()) {
      toast.error("Project name is required")
      return
    }
    ingestManualMutation.mutate({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      service: "manual" as any,
      projectName: manualProjectName.trim(),
      sha: manualSha.trim() || undefined,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      deployStatus: "succeeded" as any,
    })
  }

  const hasAnalyzing = deploys?.some(
    (deploy: DeployRow) =>
      deploy.environment === "production" && deploy.deployStatus === "succeeded" && deploy.healthStatus === "analyzing",
  )
  const releaseWaves = groupDeploysByReleaseWave(deploys ?? [])
  const releasePairingWaves = groupDeploysByReleaseWave(releasePairingDeploys ?? [])
  let releasePairingEvidence: "available" | "pending" | "unavailable" = "available"
  if (isReleasePairingLoading) releasePairingEvidence = "pending"
  else if (isReleasePairingError) releasePairingEvidence = "unavailable"
  const hasAnyActivity = hasVisibleDeploys || (allActivityProbe?.length ?? 0) > 0
  const hasFilters = Boolean(filterService || filterProject || filterHealth)
  const latestDevelopSha = stats?.latestDevelop?.sha
  const latestProductionSha = stats?.latestProduction?.sha
  const revisionsMatch = Boolean(latestDevelopSha && latestDevelopSha === latestProductionSha)
  let readinessStatusClassName = "bg-primary/10 text-primary"
  let readinessStatusLabel = "No unhealthy evaluated production releases (7d)"
  if (stats && stats.unhealthyProduction > 0) {
    readinessStatusClassName = "bg-destructive/10 text-destructive"
    readinessStatusLabel = `${stats.unhealthyProduction} unhealthy production release(s)`
  } else if (stats && (stats.evaluationPending > 0 || stats.notEvaluated > 0)) {
    readinessStatusClassName = "bg-secondary text-muted-foreground"
    readinessStatusLabel = "No evaluated health failures; coverage incomplete"
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Deploy Feed</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Unified deploy timeline with post-deploy health correlation
            {hasAnalyzing && <span className="ml-2 animate-pulse text-blue-400">● analyzing</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => void refetch()}>
            <RefreshCw className="mr-1 h-4 w-4" />
            Refresh
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" aria-label="Deploy feed administration">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setShowManualForm((value) => !value)}>
                <Plus className="h-4 w-4" />
                Add manual deploy
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setActiveTab("setup")}>
                <Settings className="h-4 w-4" />
                Webhook setup
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Tabs */}
      {activeTab === "setup" ? (
        <div>
          <button
            type="button"
            onClick={() => setActiveTab("timeline")}
            className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            ← Back to timeline
          </button>
          <WebhookSetupTab />
        </div>
      ) : (
        <>
          <Tabs value={scope} onValueChange={(value) => setScope(value as EDeployFeedScope)}>
            <TabsList aria-label="Deploy feed scope">
              <TabsTrigger value={EDeployFeedScope.RELEASES}>Releases</TabsTrigger>
              <TabsTrigger value={EDeployFeedScope.PRODUCTION}>Production only</TabsTrigger>
              <TabsTrigger value={EDeployFeedScope.ALL}>All activity</TabsTrigger>
            </TabsList>
          </Tabs>

          <ProviderIngestionPanel />

          {/* Deploy readiness */}
          {stats && (
            <>
              <div className="space-y-4 rounded-lg border border-border p-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 font-medium text-foreground">
                      <Rocket className="h-4 w-4 text-primary" />
                      VidMax web readiness
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {latestDevelopSha && latestProductionSha
                        ? revisionsMatch
                          ? "Production matches the latest develop revision."
                          : "Develop and production are on different revisions — review before promoting."
                        : "Revision comparison is unavailable until both develop and production report a SHA."}
                    </div>
                  </div>
                  <div className={`rounded-full px-3 py-1 text-xs font-medium ${readinessStatusClassName}`}>
                    {readinessStatusLabel}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  <div className="rounded-md bg-secondary p-3">
                    <div className="text-xs text-muted-foreground">Latest develop</div>
                    <div className="mt-1 font-mono text-sm font-medium">
                      {latestDevelopSha?.slice(0, 7) ?? "Unavailable"}
                    </div>
                  </div>
                  <div className="rounded-md bg-secondary p-3">
                    <div className="text-xs text-muted-foreground">Latest production</div>
                    <div className="mt-1 font-mono text-sm font-medium">
                      {latestProductionSha?.slice(0, 7) ?? "Unavailable"}
                    </div>
                  </div>
                  <div className="rounded-md bg-secondary p-3">
                    <div className="text-xs text-muted-foreground">Active releases (30m)</div>
                    <div className="mt-1 text-lg font-semibold">{stats.activeDeployments}</div>
                  </div>
                  <div className="rounded-md bg-secondary p-3">
                    <div className="text-xs text-muted-foreground">Health evaluation</div>
                    <div className="mt-1 text-sm font-medium">
                      {stats.evaluated} of {stats.eligibleProduction} eligible
                    </div>
                    <div className="text-xs text-muted-foreground">{stats.evaluationCoveragePct}% coverage</div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                {[
                  { label: "Production (7d)", value: stats.productionTotal, color: "text-foreground" },
                  { label: "Healthy", value: `${stats.healthy} (${stats.healthyPct}%)`, color: "text-green-500" },
                  { label: "Degraded", value: `${stats.degraded} (${stats.degradedPct}%)`, color: "text-yellow-500" },
                  { label: "Health failed", value: `${stats.failed} (${stats.failedPct}%)`, color: "text-red-500" },
                  { label: "Provider failed", value: stats.providerFailed, color: "text-destructive" },
                ].map((stat) => (
                  <div key={stat.label} className="rounded-lg bg-secondary p-3">
                    <div className={`text-xl font-bold ${stat.color}`}>{stat.value}</div>
                    <div className="text-xs text-muted-foreground">{stat.label}</div>
                  </div>
                ))}
              </div>
              {(stats.evaluationPending > 0 || stats.notEvaluated > 0) && (
                <div className="text-xs text-muted-foreground">
                  Evaluation coverage: {stats.evaluated} evaluated, {stats.evaluationPending} analyzing,{" "}
                  {stats.notEvaluated} not evaluated of {stats.eligibleProduction} eligible production releases.
                </div>
              )}
            </>
          )}
          {stats?.mostUnstableProject && (
            <div className="text-xs text-muted-foreground">
              Most unstable: <span className="font-medium text-yellow-500">{stats.mostUnstableProject}</span>
            </div>
          )}

          {/* DORA metrics */}
          <DORAPanel />

          {/* Project summary cards */}
          <ProjectSummaryCards />

          {/* Manual deploy form */}
          {showManualForm && (
            <div className="space-y-3 rounded-lg border border-border bg-secondary p-4">
              <div className="text-sm font-medium text-foreground">Add Manual Deploy Event</div>
              <div className="flex flex-wrap gap-3">
                <Input
                  placeholder="Project name (e.g. vidmax-api)"
                  value={manualProjectName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setManualProjectName(e.target.value)}
                  className="w-64"
                />
                <Input
                  placeholder="Git SHA (optional)"
                  value={manualSha}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setManualSha(e.target.value)}
                  className="w-40"
                />
                <Button onClick={handleAddManual} loading={ingestManualMutation.isLoading}>
                  Add Deploy
                </Button>
                <Button variant="outline" onClick={() => setShowManualForm(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <Select
              value={filterService || "all"}
              onValueChange={(value) => setFilterService(value === "all" ? "" : value)}
            >
              <SelectTrigger className="w-44" aria-label="Filter by deploy provider">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All services</SelectItem>
                <SelectItem value="vercel">Vercel</SelectItem>
                <SelectItem value="render">Render</SelectItem>
                <SelectItem value="cloudflare">Cloudflare</SelectItem>
                <SelectItem value="eas_build">EAS Build</SelectItem>
                <SelectItem value="eas_ota">EAS OTA</SelectItem>
                <SelectItem value="github_actions">GitHub Actions</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder="Filter by project..."
              value={filterProject}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterProject(e.target.value)}
              className="w-48"
            />
            <Select
              value={filterHealth || "all"}
              onValueChange={(value) => setFilterHealth(value === "all" ? "" : value)}
            >
              <SelectTrigger className="w-40" aria-label="Filter by deploy health">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All health</SelectItem>
                <SelectItem value="healthy">Healthy</SelectItem>
                <SelectItem value="degraded">Degraded</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="analyzing">Analyzing</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Deploy timeline */}
          {isLoading ? (
            <div className="rounded border-2 border-border bg-background p-8 text-center text-muted-foreground">
              <div className="mb-2 inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
              <div>Loading deploy feed...</div>
            </div>
          ) : !deploys || deploys.length === 0 ? (
            <div className="rounded border-2 border-border bg-background p-12 text-center text-muted-foreground">
              <div className="mb-3 text-4xl">🚀</div>
              <div className="mb-1 font-medium">
                {hasAnyActivity ? "No deploys match this view" : "No deploy activity yet"}
              </div>
              <div className="mb-3 text-sm">
                {hasAnyActivity
                  ? hasFilters
                    ? "Clear the filters or choose a broader deploy scope."
                    : "Choose All activity to inspect previews and other deploys."
                  : "Configure webhooks to start ingesting deploys."}
              </div>
              {hasAnyActivity ? (
                <Button variant="outline" onClick={() => setScope(EDeployFeedScope.ALL)}>
                  Show all activity
                </Button>
              ) : (
                <Button variant="outline" onClick={() => setActiveTab("setup")}>
                  <Settings className="mr-1 h-4 w-4" />
                  View Setup Guide
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {releaseWaves.map((wave) => {
                const sha = wave[0]?.sha
                const pairingWave = findReleasePairingWave(wave, releasePairingWaves)
                const waveIsRelease = wave.some(
                  (deploy) => deploy.branch === "develop" || deploy.environment === "production",
                )
                let wavePairingEvidence = releasePairingEvidence
                if (
                  wavePairingEvidence === "available" &&
                  !pairingWave &&
                  !hasReleasePairingCoverage(wave, releasePairingDeploys ?? [])
                ) {
                  wavePairingEvidence = "unavailable"
                }
                const hasWaveHeader = Boolean(
                  sha &&
                    waveIsRelease &&
                    (wave.length > 1 ||
                      wave.some(
                        (deploy) => deploy.projectName === "vidmax-vite" || deploy.projectName === "vidmax-api",
                      )),
                )
                return (
                  <div key={`deploy:${String(wave[0]?.id)}`} className={hasWaveHeader ? "space-y-2 pt-2" : undefined}>
                    {hasWaveHeader && (
                      <ReleaseWaveHeader wave={pairingWave ?? wave} pairingEvidence={wavePairingEvidence} />
                    )}
                    {wave.map((deploy) => (
                      <DeployRow
                        key={deploy.id}
                        deploy={deploy}
                        onReanalyze={handleReanalyze}
                        isReanalyzing={reanalyzingIds.has(deploy.id)}
                      />
                    ))}
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default DeployFeedScreen
